import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { useAppDispatch } from '../redux/hooks';
import { logout } from '../redux/slices/authSlice';
import { request } from '../api/request';

interface ProfileData {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  phone: string;
  gender: string;
  age: number;
  birthDate: string;
  image: string;
  address?: {
    address: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

export default function Profile() {
  const dispatch = useAppDispatch();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogout = () => {
    dispatch(logout());
  };

  const fetchProfile = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request<ProfileData>({
        method: 'GET',
        url: '/auth/me',
        requiresAuth: true,
      });
      setProfileData(response.data);
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch profile';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">Profile</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="text-gray-700 hover:text-gray-900"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  User Profile
                </h2>
                <button
                  onClick={fetchProfile}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50"
                >
                  {loading ? 'Refreshing...' : 'Refresh Profile'}
                </button>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              {profileData && (
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <img
                      src={profileData.image}
                      alt={`${profileData.firstName} ${profileData.lastName}`}
                      className="w-20 h-20 rounded-full"
                    />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {profileData.firstName} {profileData.lastName}
                      </h3>
                      <p className="text-gray-600">@{profileData.username}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Personal Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Email:</span>{' '}
                          {profileData.email}
                        </p>
                        <p>
                          <span className="font-medium">Phone:</span>{' '}
                          {profileData.phone}
                        </p>
                        <p>
                          <span className="font-medium">Gender:</span>{' '}
                          {profileData.gender}
                        </p>
                        <p>
                          <span className="font-medium">Age:</span>{' '}
                          {profileData.age}
                        </p>
                        <p>
                          <span className="font-medium">Birth Date:</span>{' '}
                          {profileData.birthDate}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-md">
                      <h4 className="font-medium text-gray-900 mb-2">
                        Address
                      </h4>
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="font-medium">Street:</span>{' '}
                          {profileData.address?.address}
                        </p>
                        <p>
                          <span className="font-medium">City:</span>{' '}
                          {profileData.address?.city}
                        </p>
                        <p>
                          <span className="font-medium">State:</span>{' '}
                          {profileData.address?.state}
                        </p>
                        <p>
                          <span className="font-medium">Postal Code:</span>{' '}
                          {profileData.address?.postalCode}
                        </p>
                        <p>
                          <span className="font-medium">Country:</span>{' '}
                          {profileData.address?.country}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <h4 className="font-medium text-gray-900 mb-2">
                      Full Profile Data (JSON)
                    </h4>
                    <pre className="text-xs bg-white p-4 rounded border overflow-auto max-h-64">
                      {JSON.stringify(profileData, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
